/**
 * Helpers de temps. Pures fonctions, aucune dependance.
 *
 * Convention du projet : les instants sont des chaines ISO 8601 **UTC**
 * (`...Z`), les jours des chaines `YYYY-MM-DD` en heure **locale**.
 */

import type { DayKey, Instant } from './types'

export const HEURE_MS = 3_600_000
export const JOUR_MS = 24 * HEURE_MS

export function nowInstant(): Instant {
  return new Date().toISOString()
}

export function toInstant(d: Date | number): Instant {
  return new Date(d).toISOString()
}

/** Millisecondes depuis l'epoch. `NaN` si la chaine est invalide. */
export function epoch(i: Instant): number {
  return Date.parse(i)
}

/** Jour calendaire local d'un instant, au format `YYYY-MM-DD`. */
export function dayKeyOf(i: Instant | Date | number): DayKey {
  const d = i instanceof Date ? i : new Date(i)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const j = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${j}`
}

export function todayKey(): DayKey {
  return dayKeyOf(new Date())
}

/** Decale une cle de jour de `n` jours (n peut etre negatif). */
export function shiftDayKey(key: DayKey, n: number): DayKey {
  const [y, m, d] = key.split('-').map(Number)
  const date = new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1)
  date.setDate(date.getDate() + n)
  return dayKeyOf(date)
}

/**
 * Valeur pour un `<input type="datetime-local">`, qui attend une heure
 * **locale** sans fuseau.
 */
export function toDatetimeLocalValue(i: Instant): string {
  const d = new Date(i)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
}

/** Inverse de `toDatetimeLocalValue` : interprete la saisie en heure locale. */
export function fromDatetimeLocalValue(v: string): Instant {
  return new Date(v).toISOString()
}

const RTF = new Intl.RelativeTimeFormat('fr', { numeric: 'auto' })

/** « il y a 3 heures », « dans 2 jours »… */
export function relatif(i: Instant, base: number = Date.now()): string {
  const diff = epoch(i) - base
  const abs = Math.abs(diff)
  if (abs < 60_000) return "a l'instant"
  if (abs < HEURE_MS) return RTF.format(Math.round(diff / 60_000), 'minute')
  if (abs < JOUR_MS) return RTF.format(Math.round(diff / HEURE_MS), 'hour')
  if (abs < 30 * JOUR_MS) return RTF.format(Math.round(diff / JOUR_MS), 'day')
  return RTF.format(Math.round(diff / (30 * JOUR_MS)), 'month')
}

const DATE_FMT = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
})

const HEURE_FMT = new Intl.DateTimeFormat('fr-FR', {
  hour: '2-digit',
  minute: '2-digit',
})

export function formatDate(i: Instant): string {
  return DATE_FMT.format(new Date(i))
}

export function formatHeure(i: Instant): string {
  return HEURE_FMT.format(new Date(i))
}

export function formatDateHeure(i: Instant): string {
  return `${formatDate(i)} a ${formatHeure(i)}`
}

/** Duree lisible : « 3 h 20 », « 2 j 4 h ». */
export function formatDuree(ms: number): string {
  if (ms < 60_000) return "moins d'une minute"
  const minutes = Math.floor(ms / 60_000)
  if (minutes < 60) return `${minutes} min`
  const heures = Math.floor(minutes / 60)
  if (heures < 24) {
    const reste = minutes % 60
    return reste ? `${heures} h ${String(reste).padStart(2, '0')}` : `${heures} h`
  }
  const jours = Math.floor(heures / 24)
  const resteH = heures % 24
  return resteH ? `${jours} j ${resteH} h` : `${jours} j`
}
